import type { AnyAction, Middleware, PreloadedState, Reducer, ReducersMapObject, Store } from 'redux';
import { applyMiddleware, combineReducers, compose, createStore } from 'redux';
import type { PersistConfig, Persistor } from 'redux-persist';
import { persistReducer, persistStore } from 'redux-persist';
import type { ThunkDispatch, ThunkMiddleware } from 'redux-thunk';
import thunkMiddleware from 'redux-thunk';

import type { StaticPagesState } from '../../../state/types';

interface CreateStoreOptions<State extends StaticPagesState> {
  readonly reducers: ReducersMapObject<State, AnyAction>;
  readonly initialState?: PreloadedState<State>;
  readonly middleware?: readonly Middleware[];
  readonly persistConfig?: PersistConfig<State>;
}

export type StoreWithDispatch<State extends StaticPagesState> = Store<State> & {
  readonly dispatch: ThunkDispatch<State, unknown, AnyAction>;
};

export interface CreateStoreResult<State extends StaticPagesState> {
  readonly store: StoreWithDispatch<State>;
  readonly persistor?: Persistor;
}

export type CreateEnhancedStore = <State extends StaticPagesState>(
  options: CreateStoreOptions<State>
) => CreateStoreResult<State>;

export const createEnhancedStore: CreateEnhancedStore = <State extends StaticPagesState>({
  reducers,
  initialState = emptyState as PreloadedState<State>,
  middleware = [],
  persistConfig,
}: CreateStoreOptions<State>) => {
  const combinedReducers = combineReducers<State>(reducers);

  const finalReducer = persistConfig
    ? (persistReducer(persistConfig, combinedReducers) as unknown as Reducer<State, AnyAction>)
    : combinedReducers;

  const customMiddleware = middleware as Middleware<unknown, State>[];
  const thunk = thunkMiddleware as ThunkMiddleware<State, AnyAction>;
  const enhancer = getEnhancer([...customMiddleware, thunk], process.env.NODE_ENV === 'production');

  const store = createStore(finalReducer, initialState, enhancer) as StoreWithDispatch<State>;

  const persistor = persistConfig ? persistStore(store) : undefined;

  return { store, persistor };
};

const getMiddleware = <T>(otherMiddleware: Middleware<unknown, T>[]) => [...otherMiddleware, thunkMiddleware];

const getEnhancer = <T extends StaticPagesState>(otherMiddleware: Middleware<unknown, T>[], isProduction: boolean) => {
  const compose = getCompose(isProduction);
  const enhancer = compose(applyMiddleware(...getMiddleware(otherMiddleware)));

  return enhancer;
};

const getCompose = (isProduction: boolean) => {
  if (isWindowWithDevTools(window)) {
    return window.reduxDevToolsExtensionCompose?.({ serialize: true, trace: !isProduction }) || compose;
  }

  return compose;
};

type WindowWithDevTools = Window & {
  readonly reduxDevToolsExtensionCompose: (options: Record<string, unknown>) => typeof compose;
};

const isWindowWithDevTools = (arg: Window | WindowWithDevTools): arg is WindowWithDevTools =>
  '__REDUX_DEVTOOLS_EXTENSION_COMPOSE__' in arg;

const emptyState: Partial<StaticPagesState> = {};
